import React, {
  useState,
  useContext,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaSearch,
  FaFilter,
  FaDownload,
  FaPrint,
  FaEye,
  FaTimes,
  FaCar,
} from "react-icons/fa";

import {
  CircleDot,
} from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import {
  useNavigate,
} from "react-router-dom";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const TyreSerialNoReport = () => {
  /* =========================================================
     THEME
  ========================================================= */

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(
    ThemeContext
  );

  const currentSkin =
    getCurrentSkin(
      skin
    );

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  const navigate =
    useNavigate();

  /* =========================================================
     SEARCH / FILTER STATES
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const [
    tyreSerialNo,
    setTyreSerialNo,
  ] = useState("");

  const [
    showFilters,
    setShowFilters,
  ] = useState(false);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    vehicleNo,
    setVehicleNo,
  ] = useState("");

  const [
    tyreSize,
    setTyreSize,
  ] = useState("");

  const [
    brand,
    setBrand,
  ] = useState("");

  const [
    pattern,
    setPattern,
  ] = useState("");

  const [
    status,
    setStatus,
  ] = useState("");

  const [
    position,
    setPosition,
  ] = useState("");

  const [
    data,
    setData,
  ] = useState([]);

  /* =========================================================
     TEMPLATE DESIGN CLASSES
     SAME VISUAL STYLE AS FIRST PAGE
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     SEARCH
     EXISTING MOCK FUNCTIONALITY PRESERVED
  ========================================================= */

  const handleSearch = () => {
    setLoading(true);

    const payload = {
      tyreSerialNo,
      vehicleNo,
      tyreSize,
      brand,
      pattern,
      status,
      position,
    };

    console.log(
      "Tyre search payload:",
      payload
    );

    /*
      EXISTING SECOND PAGE USES MOCK DATA.
      THIS LOGIC IS INTENTIONALLY KEPT.
    */

    setTimeout(() => {
      const mockData = [
        {
          id: 1,
          serialNo: "TSN001",
          vehicleNo: "KA01AB1234",
          tyreSize: "215/75R17.5",
          brand: "Michelin",
          pattern: "XZY-2",
          status: "Active",
          position: "Front Left",
          purchaseDate: "2024-01-15",
          lastService: "2024-03-20",
          kmRun: "15,234",
        },
        {
          id: 2,
          serialNo: "TSN002",
          vehicleNo: "KA02CD5678",
          tyreSize: "225/70R19.5",
          brand: "Bridgestone",
          pattern: "R250",
          status: "In Service",
          position: "Rear Right",
          purchaseDate: "2024-02-10",
          lastService: "2024-03-25",
          kmRun: "12,567",
        },
      ];

      setData(
        tyreSerialNo
          ? mockData.filter(
              (item) =>
                item.serialNo.includes(
                  tyreSerialNo
                )
            )
          : mockData
      );

      setLoading(false);
    }, 1000);
  };

  /* =========================================================
     VIEW DETAILS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleViewDetails = (
    serialNo
  ) => {
    navigate(
      `/tyre-details/${serialNo}`
    );
  };

  /* =========================================================
     EXPORT
     EXISTING FUNCTIONALITY PRESERVED
  ========================================================= */

  const handleExport = () => {
    toast.success(
      "Export functionality will be implemented"
    );
  };

  /* =========================================================
     PRINT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrint = () => {
    window.print();
  };

  /* =========================================================
     CLEAR FILTERS
     UI HELPER ONLY
  ========================================================= */

  const clearFilters = () => {
    setTyreSerialNo("");
    setVehicleNo("");
    setTyreSize("");
    setBrand("");
    setPattern("");
    setStatus("");
    setPosition("");
    setData([]);
  };

  /* =========================================================
     STATUS COLOR
  ========================================================= */

  const getStatusClass = (
    value
  ) => {
    if (
      value === "Active"
    ) {
      return isDark
        ? "bg-green-900/30 text-green-300"
        : "bg-green-100 text-green-800";
    }

    if (
      value ===
      "In Service"
    ) {
      return isDark
        ? "bg-blue-900/30 text-blue-300"
        : "bg-blue-100 text-blue-800";
    }

    return isDark
      ? "bg-red-900/30 text-red-300"
      : "bg-red-100 text-red-800";
  };

  /* =========================================================
     SUMMARY COUNTS
     ORIGINAL LOGIC PRESERVED
  ========================================================= */

  const totalTyres =
    data.length;

  const activeTyres =
    data.filter(
      (item) =>
        item.status ===
        "Active"
    ).length;

  const inServiceTyres =
    data.filter(
      (item) =>
        item.status ===
        "In Service"
    ).length;

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            Tyre Serial Number Tracking
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            SEARCH / FILTER CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              CARD HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <CircleDot className="w-5 h-5 text-white" />
              </div>

              <div>

                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  Tyre Serial Number Tracking
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  Search and monitor tyre serial numbers and vehicle assignments
                </p>
              </div>
            </div>

            {/* =============================================
                EXPORT + FILTER
            ============================================= */}

            <div className="flex flex-wrap items-center gap-3">

              {/* EXPORT
                  EXISTING EXPORT BEHAVIOR PRESERVED
              */}

              <button
                type="button"
                onClick={
                  handleExport
                }
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <FaDownload
                  size={14}
                />

                Export
              </button>

              {/* FILTER TOGGLE */}

              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    !showFilters
                  )
                }
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter
                  size={14}
                />

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ===============================================
              MAIN SERIAL NUMBER SEARCH
          =============================================== */}

          <div
            className={`px-6 py-5 border-b ${
              isDark
                ? "border-slate-700/50"
                : "border-slate-200"
            }`}
          >

            <label
              className={
                labelClass
              }
            >
              Tyre Serial Number
            </label>

            <div className="flex flex-col md:flex-row gap-3">

              <div className="relative flex-1">

                <input
                  type="text"
                  value={
                    tyreSerialNo
                  }
                  onChange={(e) =>
                    setTyreSerialNo(
                      e.target.value.toUpperCase()
                    )
                  }
                  placeholder="Enter Tyre Serial Number..."
                  className={`${inputClass} pl-10`}
                  onKeyDown={(e) => {
                    if (
                      e.key ===
                      "Enter"
                    ) {
                      handleSearch();
                    }
                  }}
                />

                <FaSearch
                  className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-400"
                  }`}
                />
              </div>

              {/* SEARCH */}

              <button
                type="button"
                onClick={
                  handleSearch
                }
                disabled={
                  loading
                }
                className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FaSearch
                    size={14}
                  />
                )}

                {loading
                  ? "Searching..."
                  : "Search"}
              </button>
            </div>
          </div>

          {/* ===============================================
              ADVANCED FILTERS
          =============================================== */}

          {showFilters && (
            <div className="p-6">

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                {/* VEHICLE NO */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Vehicle No
                  </label>

                  <input
                    type="text"
                    value={
                      vehicleNo
                    }
                    onChange={(e) =>
                      setVehicleNo(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                    placeholder="Enter vehicle number"
                  />
                </div>

                {/* TYRE SIZE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Tyre Size
                  </label>

                  <input
                    type="text"
                    value={
                      tyreSize
                    }
                    onChange={(e) =>
                      setTyreSize(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                    placeholder="Tyre size"
                  />
                </div>

                {/* BRAND */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Brand
                  </label>

                  <input
                    type="text"
                    value={
                      brand
                    }
                    onChange={(e) =>
                      setBrand(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                    placeholder="Brand name"
                  />
                </div>

                {/* PATTERN */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Pattern
                  </label>

                  <input
                    type="text"
                    value={
                      pattern
                    }
                    onChange={(e) =>
                      setPattern(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                    placeholder="Pattern type"
                  />
                </div>

                {/* STATUS */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Status
                  </label>

                  <select
                    value={
                      status
                    }
                    onChange={(e) =>
                      setStatus(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Status
                    </option>

                    <option value="Active">
                      Active
                    </option>

                    <option value="In Service">
                      In Service
                    </option>

                    <option value="Retired">
                      Retired
                    </option>

                    <option value="Damaged">
                      Damaged
                    </option>
                  </select>
                </div>

                {/* POSITION */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Position
                  </label>

                  <select
                    value={
                      position
                    }
                    onChange={(e) =>
                      setPosition(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Positions
                    </option>

                    <option value="Front Left">
                      Front Left
                    </option>

                    <option value="Front Right">
                      Front Right
                    </option>

                    <option value="Rear Left">
                      Rear Left
                    </option>

                    <option value="Rear Right">
                      Rear Right
                    </option>

                    <option value="Spare">
                      Spare
                    </option>
                  </select>
                </div>
              </div>

              {/* =============================================
                  FILTER ACTIONS
              ============================================= */}

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">

                {/* CLEAR */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes
                    size={14}
                  />

                  Clear Filters
                </button>

                {/* PRINT */}

                <button
                  type="button"
                  onClick={
                    handlePrint
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition ${
                    isDark
                      ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                      : "bg-green-100 text-green-700 hover:bg-green-200"
                  }`}
                >
                  <FaPrint
                    size={14}
                  />

                  Print
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaSearch
                      size={14}
                    />
                  )}

                  {loading
                    ? "Searching..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            SUMMARY CARDS
            EXISTING COUNTS PRESERVED
        =================================================== */}

        {data.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

            {/* TOTAL */}

            <div
              className={`rounded-2xl border p-5 shadow-sm ${
                isDark
                  ? "bg-slate-900/50 border-slate-700"
                  : "bg-white border-slate-200"
              }`}
            >
              <div className="flex items-center gap-3">

                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${baseColor}-100 text-${baseColor}-700`}
                >
                  <CircleDot
                    size={20}
                  />
                </div>

                <div>
                  <p
                    className={`text-xs font-medium ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Total Tyres
                  </p>

                  <p className="text-2xl font-bold">
                    {totalTyres}
                  </p>
                </div>
              </div>
            </div>

            {/* ACTIVE */}

            <div
              className={`rounded-2xl border p-5 shadow-sm ${
                isDark
                  ? "bg-slate-900/50 border-slate-700"
                  : "bg-white border-slate-200"
              }`}
            >
              <div className="flex items-center gap-3">

                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    isDark
                      ? "bg-green-900/30 text-green-300"
                      : "bg-green-100 text-green-700"
                  }`}
                >
                  <FaCar
                    size={18}
                  />
                </div>

                <div>
                  <p
                    className={`text-xs font-medium ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Active
                  </p>

                  <p
                    className={`text-2xl font-bold ${
                      isDark
                        ? "text-green-300"
                        : "text-green-700"
                    }`}
                  >
                    {activeTyres}
                  </p>
                </div>
              </div>
            </div>

            {/* IN SERVICE */}

            <div
              className={`rounded-2xl border p-5 shadow-sm ${
                isDark
                  ? "bg-slate-900/50 border-slate-700"
                  : "bg-white border-slate-200"
              }`}
            >
              <div className="flex items-center gap-3">

                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    isDark
                      ? "bg-blue-900/30 text-blue-300"
                      : "bg-blue-100 text-blue-700"
                  }`}
                >
                  <CircleDot
                    size={20}
                  />
                </div>

                <div>
                  <p
                    className={`text-xs font-medium ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    In Service
                  </p>

                  <p
                    className={`text-2xl font-bold ${
                      isDark
                        ? "text-blue-300"
                        : "text-blue-700"
                    }`}
                  >
                    {inServiceTyres}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===================================================
            RESULTS CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              RESULTS HEADER
          =============================================== */}

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  Tyre Tracking Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {data.length} records
                </span>
              </div>

              {data.length > 0 && (
                <span
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-500"
                  }`}
                >
                  Last updated:{" "}
                  {new Date().toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          {/* ===============================================
              LOADING
          =============================================== */}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">

              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Searching tyre records...
              </p>
            </div>
          ) : data.length > 0 ? (

            /* =============================================
               TABLE
            ============================================= */

            <div className="overflow-x-auto">

              <table className="w-full text-sm">

                {/* HEADER */}

                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Serial No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Vehicle No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Tyre Size
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Brand
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Pattern
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Status
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Position
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      KM Run
                    </th>

                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>

                {/* BODY */}

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-600"
                      : "divide-gray-200"
                  }`}
                >
                  {data.map(
                    (item) => (
                      <tr
                        key={
                          item.id
                        }
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >

                        {/* SERIAL */}

                        <td className="px-4 py-3">

                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {item.serialNo}
                          </span>
                        </td>

                        {/* VEHICLE */}

                        <td className="px-4 py-3 font-medium">
                          {item.vehicleNo}
                        </td>

                        {/* SIZE */}

                        <td className="px-4 py-3 whitespace-nowrap">
                          {item.tyreSize}
                        </td>

                        {/* BRAND */}

                        <td className="px-4 py-3">
                          {item.brand}
                        </td>

                        {/* PATTERN */}

                        <td className="px-4 py-3">
                          {item.pattern}
                        </td>

                        {/* STATUS */}

                        <td className="px-4 py-3">

                          <span
                            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusClass(
                              item.status
                            )}`}
                          >
                            {item.status}
                          </span>
                        </td>

                        {/* POSITION */}

                        <td className="px-4 py-3">
                          {item.position}
                        </td>

                        {/* KM */}

                        <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">
                          {item.kmRun}
                        </td>

                        {/* ACTION */}

                        <td className="px-4 py-3 text-center">

                          <button
                            type="button"
                            onClick={() =>
                              handleViewDetails(
                                item.serialNo
                              )
                            }
                            className={`inline-flex items-center justify-center p-1.5 rounded-full transition ${
                              isDark
                                ? "bg-blue-900/30 text-blue-300 hover:bg-blue-900/50"
                                : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                            }`}
                            title="View Details"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          ) : (

            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div className="px-4 py-14 text-center">

              <CircleDot
                size={44}
                className={`mx-auto mb-3 ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-400"
                }`}
              />

              <h3
                className={`text-sm font-semibold ${
                  isDark
                    ? "text-slate-300"
                    : "text-slate-700"
                }`}
              >
                No tyre records found
              </h3>

              <p
                className={`mt-1 text-sm ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-500"
                }`}
              >
                Try adjusting your tyre serial number or advanced filters.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default TyreSerialNoReport;